import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    question: 'What makes Berleto different from other supplements?',
    answer:
      'We combine traditional adaptogenic herbs with functional mushrooms using only the highest quality ingredients. Every formula is built on both ancient wisdom and modern clinical research — not trend-chasing.',
  },
  {
    question: 'How long until I notice a difference?',
    answer:
      'Most people feel initial benefits within 7 days of daily use. For the deeper adaptogenic benefits — stress resilience, sleep quality, hormonal balance — consistent use for 30–60 days builds the full effect.',
  },
  {
    question: 'Are your products third-party tested?',
    answer:
      'Yes. Every batch undergoes rigorous third-party testing for purity, potency, and safety. We screen for heavy metals, microbial contamination, and verify accurate dosing before any product ships.',
  },
  {
    question: 'Can I stack multiple products together?',
    answer:
      'Our formulas are designed with synergy in mind. That said, we recommend starting with one product for 2–3 weeks to understand your individual response before combining.',
  },
  {
    question: 'Do you offer a subscription?',
    answer:
      'Yes — Subscribe & Save gives you 10% off every order plus free shipping. You can pause, modify, or cancel anytime with no fees. Most of our long-term customers choose this option.',
  },
];

function FAQItem({ faq, index, isOpen, onToggle }) {
  return (
    <div
      className="border-b cursor-pointer group"
      style={{ borderColor: 'rgba(26,42,26,0.1)' }}
      onClick={onToggle}
    >
      <div className="flex items-start gap-6 py-7">
        {/* Number */}
        <span
          className="text-xs font-medium tracking-widest pt-0.5 flex-shrink-0 transition-colors duration-300"
          style={{ color: isOpen ? '#B8704D' : 'rgba(139,154,139,0.6)' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        {/* Question */}
        <p
          className="flex-1 text-base font-medium leading-snug transition-colors duration-300"
          style={{ color: isOpen ? '#1A2A1A' : '#3A4A3A' }}
        >
          {faq.question}
        </p>

        {/* Toggle icon */}
        <div
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full border transition-all duration-300"
          style={{
            borderColor: isOpen ? '#B8704D' : 'rgba(139,154,139,0.4)',
            background: isOpen ? '#B8704D' : 'transparent',
          }}
        >
          <motion.svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <line x1="5" y1="1" x2="5" y2="9" stroke={isOpen ? '#FAF9F6' : '#8B9A8B'} strokeWidth="1.5" strokeLinecap="round" />
            <line x1="1" y1="5" x2="9" y2="5" stroke={isOpen ? '#FAF9F6' : '#8B9A8B'} strokeWidth="1.5" strokeLinecap="round" />
          </motion.svg>
        </div>
      </div>

      {/* Answer */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p
              className="pb-7 pl-12 text-sm leading-relaxed"
              style={{ color: '#6B7A6B' }}
            >
              {faq.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section className="py-28 overflow-hidden" style={{ background: '#E8DCC4' }}>
      <div className="container mx-auto px-6">
        <div
          ref={ref}
          className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 items-start"
        >

          {/* Left: Header */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:sticky md:top-32"
          >
            <p
              className="text-xs font-medium tracking-[0.25em] uppercase mb-5"
              style={{ color: '#B8704D' }}
            >
              FAQ
            </p>
            <h2
              className="text-4xl md:text-5xl font-light leading-tight mb-6"
              style={{ color: '#1A2A1A' }}
            >
              Questions
              <br />
              <span style={{ color: '#8B9A8B' }}>worth asking.</span>
            </h2>
            <p
              className="text-sm leading-relaxed mb-10 max-w-xs"
              style={{ color: '#6B7A6B' }}
            >
              Everything you need to know about Berleto formulas, sourcing, and how to get the most from your routine.
            </p>

            {/* Divider */}
            <div className="w-12 h-px mb-10" style={{ background: '#B8704D' }} />

            {/* CTA */}
            <a
              href="mailto:hello@berleto.com"
              className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-70"
              style={{ color: '#1A2A1A' }}
            >
              Still have questions?
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8H13M9 4l4 4-4 4" stroke="#1A2A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </motion.div>

          {/* Right: Accordion */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Top border */}
            <div className="w-full h-px mb-0" style={{ background: 'rgba(26,42,26,0.1)' }} />

            {faqs.map((faq, i) => (
              <FAQItem
                key={i}
                faq={faq}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
